import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";
import type { ToolCategory } from "../config/toolToggles";
import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";

const TIMEOUT_MS = 30_000;
const NATIVE_CONFIRMATION_TIMEOUT_MS = 300_000;
export const NOVAPOINT_TOOL_DESCRIPTION_MAX_CHARS = 2_048;
const ACTION_SUMMARY_MAX_CHARS = 80;

export type NovapointModuleId =
    | "alignmentDesign"
    | "roadDesign"
    | "railwayDesign"
    | "waterSewerDesign"
    | "roadMarking";

export type NovapointModuleNamespace = `${NovapointModuleId}Api`;

export type NovapointModuleOperation =
    | "getCapabilities"
    | "getTasks"
    | "getTaskContext"
    | "getTaskAccessStatus"
    | "getRoadSurfaceProfiles"
    | "searchObjectTypes"
    | "prepareCreateTask"
    | "prepareEditTask"
    | "prepareAdjustLaneWidth"
    | "prepareMatchLaneWidth"
    | "prepareSetLocalizedLaneWidth"
    | "prepareTaperShoulderToZero"
    | "prepareAddBusStop"
    | "prepareCreateRoadsideObject"
    | "prepareExtendRoad"
    | "prepareRecalculateTask"
    | "prepareImportResult"
    | "prepareApplyChanges"
    | "confirmAndStartOperation"
    | "getOperationStatus"
    | "cancelOperation"
    | (string & {});

export interface NovapointModuleDescriptor {
    id: NovapointModuleId;
    displayName: string;
    namespace: NovapointModuleNamespace;
    category: Extract<
        ToolCategory,
        | "alignmentDesign"
        | "roadDesign"
        | "railwayDesign"
        | "waterSewerDesign"
        | "roadMarking"
    >;
}

export interface NovapointOperationCapability {
    operation: NovapointModuleOperation;
    method?: string;
    description?: string;
    arguments?: NovapointOperationArgument[];
    timeOutInMs?: number;
    available: boolean;
    reasonCode?: string;
    reason?: string;
}

export interface NovapointOperationArgument {
    name: string;
    type: string;
    description?: string;
    required: boolean;
}

export interface NovapointModuleCapabilities {
    contractVersion: string;
    moduleId: NovapointModuleId;
    available: boolean;
    mutationMode?:
    | "AutomaticNativeMutationAndEditorHandoff"
    | "NativeEditorHandoff"
    | "Unavailable";
    reasonCode?: string;
    reason?: string;
    operations: NovapointOperationCapability[];
}

export const NOVAPOINT_MODULE_DESCRIPTORS: readonly NovapointModuleDescriptor[] =
    [
        {
            id: "alignmentDesign",
            displayName: "Alignment Design",
            namespace: "alignmentDesignApi",
            category: "alignmentDesign",
        },
        {
            id: "roadDesign",
            displayName: "Road Design",
            namespace: "roadDesignApi",
            category: "roadDesign",
        },
        {
            id: "railwayDesign",
            displayName: "Railway Design",
            namespace: "railwayDesignApi",
            category: "railwayDesign",
        },
        {
            id: "waterSewerDesign",
            displayName: "Water & Sewer Design",
            namespace: "waterSewerDesignApi",
            category: "waterSewerDesign",
        },
        {
            id: "roadMarking",
            displayName: "Road Marking",
            namespace: "roadMarkingApi",
            category: "roadMarking",
        },
    ];

export const isNovapointModuleApiToolName = (name: string): boolean =>
    NOVAPOINT_MODULE_DESCRIPTORS.some(
        ({ namespace }) =>
            name === `WS-${namespace}` || name.startsWith(`WS-${namespace}-`),
    );

interface NovapointToolSpec {
    operation: NovapointModuleOperation;
    method?: string;
    description: (moduleName: string) => string;
    parameters: RuntimeToolDef["definition"]["parameters"];
    argOrder: string[];
    timeOutInMs?: number;
}

const TOOL_SPECS: readonly NovapointToolSpec[] = [
    {
        operation: "getCapabilities",
        description: (moduleName) =>
            `Returns runtime availability and supported operations for ${moduleName}.`,
        parameters: { type: "object", properties: {}, required: [] },
        argOrder: [],
    },
    {
        operation: "getTasks",
        description: (moduleName) =>
            `Returns the supported ${moduleName} tasks in the active Quadri model.`,
        parameters: { type: "object", properties: {}, required: [] },
        argOrder: [],
    },
    {
        operation: "getTaskContext",
        description: (moduleName) =>
            `Returns validated ${moduleName} context for one task.`,
        parameters: {
            type: "object",
            properties: {
                taskGuid: {
                    type: "string",
                    description: "The GUID of the module task.",
                },
            },
            required: ["taskGuid"],
        },
        argOrder: ["taskGuid"],
    },
    {
        operation: "getTaskAccessStatus",
        description: (moduleName) =>
            `Returns reservation, read-only, and mutation access for one ${moduleName} task.`,
        parameters: {
            type: "object",
            properties: {
                taskGuid: {
                    type: "string",
                    description: "The GUID of the module task.",
                },
            },
            required: ["taskGuid"],
        },
        argOrder: ["taskGuid"],
    },
    {
        operation: "prepareCreateTask",
        description: (moduleName) =>
            `Prepares creation of a new ${moduleName} task in its native Novapoint editor. Returns an opaque operation ID that must be confirmed before the editor starts.`,
        parameters: {
            type: "object",
            properties: {
                taskName: {
                    type: "string",
                    description: "Name for the new Novapoint task.",
                },
                changeRequest: {
                    type: "string",
                    description:
                        "A concise description of the alignment or design to create.",
                },
            },
            required: ["taskName"],
        },
        argOrder: ["taskName", "changeRequest"],
    },
    {
        operation: "prepareEditTask",
        description: (moduleName) =>
            `Prepares a manual ${moduleName} editor handoff. The changeRequest is shown for review but is not parsed or applied by this API. After confirmation, the user must complete and save the change in the native editor.`,
        parameters: {
            type: "object",
            properties: {
                taskGuid: {
                    type: "string",
                    description: "The GUID of the Novapoint task to edit.",
                },
                changeRequest: {
                    type: "string",
                    description:
                        "The requested domain change, including values and units where applicable.",
                },
            },
            required: ["taskGuid", "changeRequest"],
        },
        argOrder: ["taskGuid", "changeRequest"],
    },
    {
        operation: "prepareAdjustLaneWidth",
        description: () =>
            "Prepares an automatic Road lane-width mutation. After trusted confirmation, the API adds deltaMeters to every width definition on the selected VIPS surface, preserves its metadata, chainages, and slopes, saves the authoritative task attachment, and reopens it to verify the persisted values. It does not recalculate the Road result.",
        parameters: {
            type: "object",
            properties: {
                taskGuid: {
                    type: "string",
                    description: "The GUID of the writable Road Design task.",
                },
                surfaceNumber: {
                    type: "number",
                    description:
                        "The selected Road lane's VIPS surface number, for example -1.01.",
                },
                deltaMeters: {
                    type: "number",
                    description:
                        "The signed width change in meters, for example 0.5 to widen by half a meter.",
                },
            },
            required: ["taskGuid", "surfaceNumber", "deltaMeters"],
        },
        argOrder: ["taskGuid", "surfaceNumber", "deltaMeters"],
    },
    {
        operation: "prepareRecalculateTask",
        description: (moduleName) =>
            moduleName === "Road Design"
                ? "Prepares an automatic Road build and result refresh. After trusted confirmation, Novapoint recalculates the design model, verifies that the .dmr result changed, applies the refreshed task execution in Quadri, and returns Completed with verified and resultRefreshed evidence."
                : `Prepares the native ${moduleName} build/recalculation workflow for an existing task. Returns an operation ID requiring confirmation.`,
        parameters: {
            type: "object",
            properties: {
                taskGuid: {
                    type: "string",
                    description: "The GUID of the Novapoint task to recalculate.",
                },
            },
            required: ["taskGuid"],
        },
        argOrder: ["taskGuid"],
    },
    {
        operation: "prepareImportResult",
        description: (moduleName) =>
            `Prepares the native ${moduleName} result-import workflow for an existing task. The authoring application remains responsible for producing the result.`,
        parameters: {
            type: "object",
            properties: {
                taskGuid: {
                    type: "string",
                    description: "The GUID of the task whose result should be imported.",
                },
            },
            required: ["taskGuid"],
        },
        argOrder: ["taskGuid"],
    },
    {
        operation: "prepareApplyChanges",
        description: (moduleName) =>
            `Prepares applying ${moduleName} changes through its native authoring workflow. Returns an operation ID requiring confirmation.`,
        parameters: {
            type: "object",
            properties: {
                taskGuid: {
                    type: "string",
                    description: "The GUID of the Novapoint task to update.",
                },
                changeRequest: {
                    type: "string",
                    description: "The changes to review and apply in the native editor.",
                },
            },
            required: ["taskGuid", "changeRequest"],
        },
        argOrder: ["taskGuid", "changeRequest"],
    },
    {
        operation: "confirmAndStartOperation",
        description: (moduleName) =>
            `Displays trusted native confirmation for a prepared ${moduleName} operation. Automatic mutations return Completed only after the authoritative attachment is saved and verified; automatic Road recalculations return Completed only after the refreshed result is verified and applied in Quadri; manual operations return Started when the native editor launches and still require the user to complete and save the change.`,
        parameters: {
            type: "object",
            properties: {
                operationId: {
                    type: "string",
                    description: "The opaque ID returned by a prepare operation.",
                },
            },
            required: ["operationId"],
        },
        argOrder: ["operationId"],
        timeOutInMs: NATIVE_CONFIRMATION_TIMEOUT_MS,
    },
    {
        operation: "getOperationStatus",
        description: (moduleName) =>
            `Returns the state and evidence for a prepared ${moduleName} operation. A Completed automatic mutation includes persisted before/after evidence; a Completed automatic Road recalculation includes verified and resultRefreshed evidence; a Started manual operation is only an editor handoff and cannot verify user edits.`,
        parameters: {
            type: "object",
            properties: {
                operationId: {
                    type: "string",
                    description: "The opaque operation ID.",
                },
            },
            required: ["operationId"],
        },
        argOrder: ["operationId"],
    },
    {
        operation: "cancelOperation",
        description: (moduleName) =>
            `Cancels a ${moduleName} operation if native editing or apply has not started.`,
        parameters: {
            type: "object",
            properties: {
                operationId: {
                    type: "string",
                    description: "The opaque operation ID.",
                },
            },
            required: ["operationId"],
        },
        argOrder: ["operationId"],
    },
];

const parseJson = (value: unknown): unknown => {
    if (typeof value !== "string") return value;
    return JSON.parse(value);
};

export const parseNovapointModuleCapabilities = (
    descriptor: NovapointModuleDescriptor,
    value: unknown,
): NovapointModuleCapabilities => {
    const parsed = parseJson(value) as Partial<NovapointModuleCapabilities>;
    if (!parsed || parsed.moduleId !== descriptor.id) {
        throw new Error(
            `Capability response does not match ${descriptor.namespace}`,
        );
    }
    if (typeof parsed.contractVersion !== "string") {
        throw new Error(`Missing contractVersion for ${descriptor.namespace}`);
    }
    if (typeof parsed.available !== "boolean" || !Array.isArray(parsed.operations)) {
        throw new Error(`Invalid capability response for ${descriptor.namespace}`);
    }

    return parsed as NovapointModuleCapabilities;
};

const createCallback = (
    namespace: NovapointModuleNamespace,
    method: string,
    argOrder: string[],
): RuntimeToolDef["callback"] => {
    return async (args: Record<string, unknown>): Promise<string> => {
        const api = getAPI();
        const moduleApi = api?.[namespace];
        const fn = moduleApi?.[method];
        if (typeof fn !== "function") {
            throw new Error(`CefSharp method ${namespace}.${method} is unavailable`);
        }

        const result = await invokeCefSharpMethod<unknown>(
            namespace,
            moduleApi,
            method,
            argOrder.map((name) => args[name]),
        );
        return typeof result === "string" ? result : JSON.stringify(result);
    };
};

const hasArgument = (args: Record<string, unknown>, name: string): boolean => {
    const value = args[name];
    return value !== undefined && value !== null && value !== "";
};

const getAvailableSpecs = (
    capabilities: NovapointModuleCapabilities,
): NovapointToolSpec[] => {
    const legacySpecs = new Map(
        TOOL_SPECS.map((spec) => [spec.operation, spec] as const),
    );
    const specs = capabilities.operations
        .filter((capability) => capability.available)
        .map((capability): NovapointToolSpec | undefined => {
            if (
                typeof capability.description === "string" &&
                Array.isArray(capability.arguments)
            ) {
                const properties = Object.fromEntries(
                    capability.arguments.map((argument) => [
                        argument.name,
                        {
                            type: argument.type,
                            description: argument.description,
                        },
                    ]),
                );

                return {
                    operation: capability.operation,
                    method: capability.method ?? capability.operation,
                    description: () => capability.description!,
                    parameters: {
                        type: "object",
                        properties,
                        required: capability.arguments
                            .filter((argument) => argument.required)
                            .map((argument) => argument.name),
                    },
                    argOrder: capability.arguments.map(
                        (argument) => argument.name,
                    ),
                    timeOutInMs: capability.timeOutInMs,
                };
            }

            return legacySpecs.get(capability.operation);
        })
        .filter((spec): spec is NovapointToolSpec => spec !== undefined);

    if (!specs.some((spec) => spec.operation === "getCapabilities")) {
        specs.unshift(legacySpecs.get("getCapabilities")!);
    }

    return capabilities.available
        ? specs
        : specs.filter((spec) => spec.operation === "getCapabilities");
};

const createModuleToolDescription = (
    descriptor: NovapointModuleDescriptor,
    specs: readonly NovapointToolSpec[],
): string => {
    const signatures = specs.map((spec) => {
        const required = new Set(spec.parameters.required ?? []);
        const argumentsList = spec.argOrder
            .map((argument) =>
                required.has(argument) ? argument : `[${argument}]`,
            )
            .join(", ");
        return `${spec.operation}(${argumentsList})`;
    });
    const roadRouting = descriptor.id === "roadDesign"
        ? " For a localized lane or bus stop, pass the semantic side; the host inspects the Road model and selects or creates the auxiliary lane. Never guess or provide a VIPS surface number. For prepareAddBusStop, set suppressShoulder to false unless the user explicitly requested shoulder suppression. For a shelter, sign, bench, lamp, cabinet, or other discrete roadside object, call searchObjectTypes with geometryType point, then prepareCreateRoadsideObject with the user's natural-language type. Never provide catalogue IDs."
        : "";
    const prefix = `Use this single tool for ${descriptor.displayName}. Choose an action and provide its listed arguments.${roadRouting} `;
    const suffix =
        " Prepare changes first, then confirmAndStartOperation(operationId). Automatic mutations return Completed only after verification; manual operations return Started and still require completion in Novapoint. Never repeat a prepare action after it returns Completed.";
    const compactActions = specs
        .map((spec, index) => {
            const summary = spec
                .description(descriptor.displayName)
                .replace(/\s+/g, " ")
                .trim();
            const shortenedSummary =
                summary.length <= ACTION_SUMMARY_MAX_CHARS
                    ? summary
                    : `${summary.slice(0, ACTION_SUMMARY_MAX_CHARS - 3).trimEnd()}...`;
            return `${signatures[index]} - ${shortenedSummary}`;
        })
        .join("; ");
    const description = `${prefix}Actions: ${compactActions}.${suffix}`;

    if (description.length <= NOVAPOINT_TOOL_DESCRIPTION_MAX_CHARS) {
        return description;
    }

    const signaturesOnly = `${prefix}Actions: ${signatures.join("; ")}.${suffix}`;
    return signaturesOnly.slice(0, NOVAPOINT_TOOL_DESCRIPTION_MAX_CHARS);
};

export const createNovapointModuleApiTools = (
    descriptor: NovapointModuleDescriptor,
    capabilities: NovapointModuleCapabilities,
): Record<string, RuntimeToolDef> => {
    const specs = getAvailableSpecs(capabilities);
    const specsByOperation = new Map(
        specs.map((spec) => [spec.operation, spec] as const),
    );
    const properties: Record<string, any> = {
        action: {
            type: "string",
            enum: specs.map((spec) => spec.operation),
            description: `The ${descriptor.displayName} action to perform.`,
        },
    };

    for (const spec of specs) {
        Object.assign(properties, spec.parameters.properties);
    }

    const name = `WS-${descriptor.namespace}`;
    return {
        [name]: {
            definition: {
                name,
                description: createModuleToolDescription(descriptor, specs),
                deferred: true,
                title: toToolDisplayName(name),
                parameters: {
                    type: "object",
                    properties,
                    required: ["action"],
                },
            },
            callback: async (args: Record<string, unknown>): Promise<string> => {
                const action = args.action;
                if (typeof action !== "string") {
                    throw new Error(`Missing action for ${descriptor.namespace}`);
                }

                const spec = specsByOperation.get(
                    action as NovapointModuleOperation,
                );
                if (!spec) {
                    throw new Error(
                        `${action} is unavailable for ${descriptor.namespace}`,
                    );
                }

                const missing = (spec.parameters.required ?? []).filter(
                    (argument) => !hasArgument(args, argument),
                );
                if (missing.length > 0) {
                    throw new Error(
                        `${action} requires: ${missing.join(", ")}`,
                    );
                }

                return createCallback(
                    descriptor.namespace,
                    spec.method ?? spec.operation,
                    spec.argOrder,
                )(args);
            },
            timeOutInMs: Math.max(
                TIMEOUT_MS,
                ...specs.map((spec) => spec.timeOutInMs ?? TIMEOUT_MS),
            ),
        },
    };
};
